<template>
  <div class="flex-1 flex flex-col">
    <Actions
      :disabled="loading"
      @set-monthly-view="$emit('set-monthly-view')"
      @prev-week="$emit('prev-week')"
      @next-week="$emit('next-week')"
      @today="$emit('set-date', calendar.meta.now)"
    >
      <h6 class="my-auto ml-2 font-medium">
        {{ calendar.meta.focusedDate.format('YYYY MMMM') }}
      </h6>
      <div class="flex items-center ml-auto mr-4">
        <span class="inline-block mr-2">View</span>
        <label class="relative">
          <input
            :checked="assignedToMe"
            :value="false"
            type="radio"
            name="assigned"
            class="field opacity-0 absolute inset-0 w-full h-full"
            @change="$emit('update:assigned-to-me', false)"
          />
          <span
            class="inline-block px-3 py-2 bg-white leading-6 rounded-l field-checked:bg-blue-600 field-checked:text-white transition-colors duration-75"
          >
            All
          </span>
        </label>
        <label class="relative">
          <input
            :checked="assignedToMe"
            :value="true"
            type="radio"
            name="assigned"
            class="field opacity-0 absolute inset-0 w-full h-full"
            @change="$emit('update:assigned-to-me', true)"
          />
          <span
            class="inline-block px-3 py-2 bg-white leading-6 rounded-r field-checked:bg-blue-600 field-checked:text-white transition-colors duration-75"
          >
            Assigned to me
          </span>
        </label>
      </div>
    </Actions>
    <CalHeader :week-days="calendar.header" />

    <div v-if="loading" key="loading_weekly" class="flex-1 flex border-t border-gray-600">
      <Loader class="m-auto" />
    </div>
    <div v-else :key="`week_${calendar.meta.week}`" class="flex-1 flex border-t border-gray-600">
      <CalDate
        v-for="day in week.days"
        :key="`${week.number}_${day.day.date()}`"
        v-bind="{ week: week.number, day: day.day, meta: day.meta }"
        @set-date="$emit('set-date', arguments[0])"
      >
        <template v-if="tasksByDate[day.day.format('YYYY-MM-DD')]">
          <TaskCard
            v-for="task in tasksByDate[day.day.format('YYYY-MM-DD')]"
            :task="task"
            :key="task.id"
            :is-focused-day="day.meta.isFocusedDay"
            @edit:task="handleTaskClick"
          />
        </template>

        <template v-if="campaignsByStartingDate[day.day.format('YYYY-MM-DD')]">
          <CampaignCard
            v-for="campaign in campaignsByStartingDate[day.day.format('YYYY-MM-DD')]"
            :key="campaign.id"
            :day="day"
            :campaign="campaign"
            starting
            :focused="$route.query.campaignId === campaign.id"
          />
        </template>

        <template v-if="campaignsByEndingDate[day.day.format('YYYY-MM-DD')]">
          <CampaignCard
            v-for="campaign in campaignsByEndingDate[day.day.format('YYYY-MM-DD')]"
            :key="campaign.id"
            :day="day"
            :campaign="campaign"
            :focused="$route.query.campaignId === campaign.id"
          />
        </template>

        <template v-if="productionOrdersByDate[day.day.format('YYYY-MM-DD')]">
          <ProductionOrderCard
            v-for="productionOrder in productionOrdersByDate[day.day.format('YYYY-MM-DD')]"
            :production-order="productionOrder"
            :key="productionOrder.uuid"
            :is-focused-day="day.meta.isFocusedDay"
            @edit:production-order="handleProductionOrderClick"
          />
        </template>
      </CalDate>
    </div>
  </div>
</template>

<script>
import { isEqual } from 'lodash-es';
import Actions from './weekly/Actions.vue';
import CalHeader from './weekly/Header.vue';
import CalDate from './weekly/Date.vue';
import CampaignCard from './weekly/CampaignCard.vue';
import TaskCard from './weekly/TaskCard.vue';
import ProductionOrderCard from './weekly/ProductionOrderCard.vue';

export default {
  name: 'Weekly',
  components: {
    Actions,
    CalHeader,
    CalDate,
    CampaignCard,
    TaskCard,
    ProductionOrderCard,
  },
  props: {
    calendar: {
      type: Object,
      required: true,
    },
    tasks: {
      type: Array,
      default: () => [],
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
    productionOrders: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    assignedToMe: Boolean,
  },
  data: () => ({
    currentTaskable: null,
  }),
  computed: {
    week() {
      return this.calendar.weeks.find((week) => week.number === this.calendar.meta.week);
    },
    tasksByDate() {
      const tasksByDate = {};

      this.tasks.forEach((task) => {
        const [date] = task.dueAt.split(' ');
        if (typeof tasksByDate[date] === 'undefined') tasksByDate[date] = [];
        tasksByDate[date].push(task);
      });

      return tasksByDate;
    },
    campaignsByStartingDate() {
      const campaignsByStartingDate = {};

      this.campaigns
        .filter((campaign) => ['both', 'start'].indexOf(campaign.datesVisibleOnCalendar) !== -1)
        .filter((campaign) => campaign.dateStart)
        .forEach((campaign) => {
          const date = campaign.dateStart;

          if (typeof campaignsByStartingDate[date] === 'undefined') {
            campaignsByStartingDate[date] = [];
          }

          campaignsByStartingDate[date].push(campaign);
        });

      return campaignsByStartingDate;
    },
    campaignsByEndingDate() {
      const campaignsByEndingDate = {};

      this.campaigns
        .filter((campaign) => ['both', 'end'].indexOf(campaign.datesVisibleOnCalendar) !== -1)
        .filter((campaign) => campaign.dateEnd)
        .forEach((campaign) => {
          const date = campaign.dateEnd;

          if (typeof campaignsByEndingDate[date] === 'undefined') campaignsByEndingDate[date] = [];
          campaignsByEndingDate[date].push(campaign);
        });

      return campaignsByEndingDate;
    },
    productionOrdersByDate() {
      const productionOrdersByDate = {};

      this.productionOrders.forEach((productionOrder) => {
        const parts = productionOrder.deliveryAt.split(' ');
        const date = parts[0];

        if (typeof productionOrdersByDate[date] === 'undefined') productionOrdersByDate[date] = [];
        productionOrdersByDate[date].push(productionOrder);
      });

      return productionOrdersByDate;
    },
  },
  methods: {
    handleTaskClick(task) {
      const { currentRoute } = this.$router;
      const queryParams = {
        taskId: task.uuid,
        editTaskId: task.uuid,
      };

      if (task.contactId) {
        queryParams.editContact = task.contactId;
      } else if (task.contactRequestId) {
        queryParams.editContactRequest = task.contactREquestId;
      }

      if (isEqual(queryParams, currentRoute.query)) {
        return;
      }

      this.$router.replace({
        name: currentRoute.name,
        params: { ...currentRoute.params },
        query: queryParams,
      });
    },
    handleProductionOrderClick(productionOrder) {
      if (!productionOrder.orderItems || !productionOrder.orderItems.length) {
        return;
      }
      const { currentRoute } = this.$router;
      const queryParams = {
        editProductionOrderItemId: productionOrder.orderItems[0],
        productionOrderItemId: productionOrder.orderItems[0],
      };

      if (isEqual(queryParams, currentRoute.query)) {
        return;
      }

      this.$router.replace({
        name: currentRoute.name,
        params: { ...currentRoute.params },
        query: queryParams,
      });
    },
  },
};
</script>
